import React from 'react';
import { alpha, styled } from '@mui/material';
import { IconSize, Icon } from '../../../Icon';
import { Typography } from '../../../Typography';
import { SPACING, RADIUS, COLORS, TYPOGRAPHY } from '../../constants';

const TriggerButtonStyled = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: `${SPACING.sm}px`,
  padding: `${SPACING.lg}px 18px`,
  backgroundColor: COLORS.bgPrimary,
  border: `1px solid ${COLORS.borderPrimary}`,
  borderRadius: `${RADIUS.md}px`,
  boxShadow: `0px 1px 2px 0px ${COLORS.shadowXs}`,
  cursor: 'pointer',
  width: '100%',
  justifyContent: 'flex-start',
  fontFamily: 'inherit',
  position: 'relative',
  // Skeuomorphic inner shadows
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    borderRadius: `${RADIUS.md}px`,
    boxShadow: `inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05)`,
  },
  '&:hover': {
    backgroundColor: COLORS.bgHover,
  },
  '&:focus': {
    outline: 'none',
    boxShadow: `0px 1px 2px 0px ${COLORS.shadowXs}, 0px 0px 0px 2px ${COLORS.bgPrimary}, 0px 0px 0px 4px ${alpha(
      theme.palette.primary.main,
      0.24,
    )}`,
  },
  '&:disabled': {
    backgroundColor: COLORS.bgHover,
    cursor: 'not-allowed',
    '& .button-text': {
      color: COLORS.textDisabled,
    },
  },
}));

export type TriggerButtonProps = {
  id?: string;
  disabled?: boolean;
  displayText: string;
  hasSelectedDates: boolean;
  ariaLabel?: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  ariaDescribedBy?: string;
};

export const TriggerButton: React.FC<TriggerButtonProps> = ({
  id,
  disabled = false,
  displayText,
  hasSelectedDates,
  ariaLabel,
  onClick,
  ariaDescribedBy,
}) => {
  return (
    <TriggerButtonStyled
      id={id}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
    >
      <Icon size={IconSize.MD} folder="design-system/calendar" />
      <Typography
        variant="body1"
        className="button-text"
        component="p"
        sx={{
          fontWeight: 600,
          ...TYPOGRAPHY.textMd,
          color: hasSelectedDates ? COLORS.textSecondary : COLORS.textPlaceholder,
        }}
      >
        {displayText}
      </Typography>
    </TriggerButtonStyled>
  );
};
